import * as React from 'react'
import { useContext } from 'react'
import { Dialog } from '@highlight/ui'
import { ModalContent } from './nested/modal-content'
import { ModalRenderer } from './modal-renderer'
import { ModalBackdrop } from './nested/modal-backdrop'
import { ModalPopup } from './nested/modal-popup'
import { ModalContext } from './nested/modal-context'

interface ModalProps extends Omit<React.ComponentProps<typeof Dialog>, 'confirm'> {
  confirm?: boolean
  disableEscape?: boolean
}

/**
 * Modal component with Framer Motion animations.
 * Uses Dialog from @highlight/ui as the base with animated Content.
 * Automatically wires context values when used within ModalRenderer.
 * Set confirm={true} for a confirm dialog: it shows no close button by default.
 */
function Modal({ children, open, onOpenChange, confirm, disableEscape, ...props }: ModalProps) {
  const context = useContext(ModalContext)

  const isConfirm = confirm ?? context?.confirm ?? false

  // Use context values if available, otherwise use props
  const isOpen = context ? true : open
  const handleOpenChange = context
    ? (newOpen: boolean) => {
        if (!newOpen) context.close()
      }
    : onOpenChange

  const shouldUpdateContext = confirm !== undefined && confirm !== context?.confirm

  const content = (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      disableEscape={disableEscape}
      {...props}
    >
      {children}
    </Dialog>
  )

  if (shouldUpdateContext && context) {
    return <ModalContext.Provider value={{ ...context, confirm: isConfirm }}>{content}</ModalContext.Provider>
  }

  return content
}

// Export Dialog sub-components by default
Modal.Trigger = Dialog.Trigger
Modal.Portal = Dialog.Portal
Modal.Backdrop = ModalBackdrop
Modal.Popup = ModalPopup
Modal.Title = Dialog.Title
Modal.Description = Dialog.Description
Modal.Close = Dialog.Close
Modal.Content = ModalContent
Modal.Header = Dialog.Header
Modal.Body = Dialog.Body
Modal.Footer = Dialog.Footer

Modal.Renderer = ModalRenderer

export { Modal }
export { useModals, useOpenModals, useIsModalOpen } from './atoms'
export { useModalContext } from './nested/modal-context'
